import React, { useState, useRef, useEffect } from 'react';
import { Button, message, Spin, Dropdown, Menu, Tooltip } from 'antd';
import { RobotOutlined, CloseOutlined, MessageOutlined, MoreOutlined, ExpandOutlined, CompressOutlined, MobileOutlined, TabletOutlined, DesktopOutlined } from '@ant-design/icons';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import aiChatApi, { ChatModule, ChatMessage as ChatMessageType, StreamingChatResponse } from '../../api/aiChatApi';

interface FloatingChatWidgetProps {
  isOpen: boolean;
  onToggle: () => void;
}

type WindowSize = 'mobile' | 'tablet' | 'desktop' | 'fullscreen';

const FloatingChatWidget: React.FC<FloatingChatWidgetProps> = ({ isOpen, onToggle }) => {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [loading, setLoading] = useState(false);
  const [showQuickPrompts, setShowQuickPrompts] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [windowSize, setWindowSize] = useState<WindowSize>('desktop');
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamCleanupRef = useRef<(() => void) | null>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Load messages from localStorage on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem('aiChatMessages');
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        setMessages(parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        })));
      } catch (error) {
        console.error('Error loading saved messages:', error);
      }
    }
  }, []);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('aiChatMessages', JSON.stringify(messages));
    }
  }, [messages]);

  // Cleanup streaming on unmount
  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
      }
    };
  }, []);

  const handleSendMessage = async (
    prompt: string,
    module: ChatModule,
    dateRange?: string[],
    categories?: string[]
  ) => {
    // Add user message
    const userMessage = aiChatApi.createMessage('user', prompt);
    setMessages((prev) => [...prev, userMessage]);

    // Create initial assistant message for streaming
    const assistantMessageId = aiChatApi.createMessage('assistant', '').id;
    setStreamingMessageId(assistantMessageId);
    setMessages((prev) => [...prev, { ...aiChatApi.createMessage('assistant', ''), id: assistantMessageId }]);

    setLoading(true);

    try {
      // Clean up any existing stream
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
      }

      // Send streaming query to backend
      const cleanup = await aiChatApi.sendStreamingQuery(
        { prompt, module, dateRange, categories, includeMeta: false },
        // onChunk
        (chunk: StreamingChatResponse) => {
          if (chunk.type === 'chunk' && chunk.content) {
            setMessages((prev) => 
              prev.map((msg) => 
                msg.id === assistantMessageId 
                  ? { ...msg, content: msg.content + chunk.content }
                  : msg
              )
            );
          }
        },
        // onComplete
        (finalResponse: StreamingChatResponse) => {
          setMessages((prev) => 
            prev.map((msg) => 
              msg.id === assistantMessageId 
                ? { 
                    ...msg, 
                    content: finalResponse.content || msg.content,
                    data: finalResponse.data,
                    metadata: finalResponse.metadata
                  }
                : msg
            )
          );
          setStreamingMessageId(null);
          setLoading(false);
          streamCleanupRef.current = null;
        },
        // onError
        (error: Error) => {
          message.error(error.message || 'Failed to get response from AI');
          
          // Update the streaming message with error
          setMessages((prev) => 
            prev.map((msg) => 
              msg.id === assistantMessageId 
                ? { 
                    ...msg, 
                    content: `Sorry, I encountered an error: ${error.message}. Please try again or rephrase your question.`
                  }
                : msg
            )
          );
          setStreamingMessageId(null);
          setLoading(false);
          streamCleanupRef.current = null;
        }
      );

      streamCleanupRef.current = cleanup || null;
    } catch (error: any) {
      message.error(error.message || 'Failed to setup streaming');
      setStreamingMessageId(null);
      setLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    localStorage.removeItem('aiChatMessages');
    message.success('Chat history cleared');
  };

  // Close all options when chat is closed
  const handleChatToggle = () => {
    setShowQuickPrompts(false);
    setShowFilters(false);
    onToggle();
  };

  // Window size configurations
  const getWindowDimensions = (size: WindowSize) => {
    switch (size) {
      case 'mobile':
        return { width: '320px', height: '400px' };
      case 'tablet':
        return { width: '480px', height: '500px' };
      case 'desktop':
        return { width: '600px', height: '600px' };
      case 'fullscreen':
        return { width: '90vw', height: '90vh' };
      default:
        return { width: '600px', height: '600px' };
    }
  };

  // Window size dropdown menu
  const windowSizeMenu = (
    <Menu className="w-48">
      <Menu.Item 
        key="mobile" 
        icon={<MobileOutlined />}
        onClick={() => setWindowSize('mobile')}
        className={windowSize === 'mobile' ? 'bg-blue-50' : ''}
      >
        Mobile (320px)
      </Menu.Item>
      <Menu.Item 
        key="tablet" 
        icon={<TabletOutlined />}
        onClick={() => setWindowSize('tablet')}
        className={windowSize === 'tablet' ? 'bg-blue-50' : ''}
      >
        Tablet (480px)
      </Menu.Item>
      <Menu.Item 
        key="desktop" 
        icon={<DesktopOutlined />}
        onClick={() => setWindowSize('desktop')}
        className={windowSize === 'desktop' ? 'bg-blue-50' : ''}
      >
        Desktop (600px)
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item 
        key="fullscreen" 
        icon={<ExpandOutlined />}
        onClick={() => setWindowSize('fullscreen')}
        className={windowSize === 'fullscreen' ? 'bg-blue-50' : ''}
      >
        Fullscreen (90vw)
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      {/* Floating Chat Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <div className="relative">
          {/* Pulsing ring animation */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-400 to-blue-400 animate-ping opacity-20"></div>
          <Tooltip title="ParkingLap AI Assistant" placement="left">
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={<RobotOutlined />}
              onClick={handleChatToggle}
              className="shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 relative"
              style={{
                width: 60,
                height: 60,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
              }}
            />
          </Tooltip>
        </div>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-6 right-6 z-50 bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
          style={getWindowDimensions(windowSize)}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RobotOutlined className="text-white text-lg" />
              <span className="text-white font-semibold">ParkingLap AI Assistant</span>
              
              {/* 3-dots menu for window size */}
              <Dropdown
                overlay={windowSizeMenu}
                trigger={['click']}
                placement="bottomLeft"
              >
                <Button
                  type="text"
                  icon={<MoreOutlined />}
                  className="text-white hover:bg-white/20 ml-2"
                  size="small"
                  title="Window size options"
                />
              </Dropdown>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <Button
                  type="text"
                  icon={<MessageOutlined />}
                  onClick={handleClearChat}
                  className="text-white hover:bg-white/20"
                  size="small"
                />
              )}
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleChatToggle}
                className="text-white hover:bg-white/20"
                size="small"
              />
            </div>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
            {messages.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <RobotOutlined className="text-4xl text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-2">Welcome to ParkingLap AI Assistant!</p>
                  <p className="text-sm text-gray-500">
                    I'm here to help you analyze and understand your ParkingLap data.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((message, idx) => (
                  <ChatMessage 
                    key={message.id} 
                    message={message}
                    isStreaming={streamingMessageId === message.id}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area - Full Height */}
          <div className="bg-white flex-shrink-0">
            <ChatInput 
              onSendMessage={handleSendMessage} 
              loading={loading} 
              showQuickPrompts={showQuickPrompts}
              showFilters={showFilters}
              onToggleQuickPrompts={() => {
                setShowQuickPrompts(!showQuickPrompts);
                // Close filters when opening quick prompts
                if (!showQuickPrompts && showFilters) {
                  setShowFilters(false);
                }
              }}
              onToggleFilters={() => {
                setShowFilters(!showFilters);
                // Close quick prompts when opening filters
                if (!showFilters && showQuickPrompts) {
                  setShowQuickPrompts(false);
                }
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingChatWidget;
